import { Box, Typography, useToastStore } from '@branch-messenger/willow-ui'
import { InfoCircle } from '@branch-messenger/willow-ui/icons'
import { use } from 'react'

import { Alert, AlertBodyText } from '@/components/alert'
import { DialogContext } from '@/context'
import { formatAccountNumberLast4 } from '@/lib/string-formatting'
import { useUpdatePaymentProfile } from '@/queries/profile'
import { useFormattedOrgFees, useOrg } from '@/selectors/organization'
import { useMappedPaymentProfile } from '@/selectors/profile'

import { AppPayoutsModalContent } from '../AppPayoutsModalContent'
import { DebitCardModalContent } from '../DebitCardModalContent'
import { DirectCardPrimerModalContent } from '../DirectCardPrimerModalContent'

export interface AccountAction {
  iconName: string
  title: string
  description: string
  visible?: boolean
  handleClick?: () => void
}

export const useAccountActions = () => {
  const updatePaymentProfile = useUpdatePaymentProfile()

  const { showAlert, showDialog } = use(DialogContext)
  const addToast = useToastStore(state => state.addToast)

  const {
    data: { orgName, orgInfo },
  } = useOrg()

  const { data } = useFormattedOrgFees()
  const { data: paymentProfile } = useMappedPaymentProfile()
  const {
    branch_direct_card_account,
    active_type,
    wallet,
    accountTypeDisplay,
  } = paymentProfile || {}

  const { baseFee, feePercentage, orgCoversAllFees } = data || {}

  const switchToDebitCard = () => {
    const switchVerb = active_type === 'NONE' ? 'set' : 'switch'
    showAlert({
      title: 'Are you sure?',
      description: branch_direct_card_account ? (
        <Box $display="flex" $direction="column" $gap={4} $align="flex-start">
          <Typography>
            {`You are about to ${switchVerb} your payout method to your connected ${branch_direct_card_account.network} debit card ending in ${branch_direct_card_account.last4}, all new payouts will be sent there.`}
          </Typography>
          <Typography>
            You can stop payments or update your payment method at any time.
          </Typography>
        </Box>
      ) : (
        `You are about to ${switchVerb} your payout method to a debit card, all new payouts will be sent there once it's connected.`
      ),
      actionDescription: 'Yes, Continue',
      cancelDescription: 'No, Cancel',
      children: (
        <Alert Icon={InfoCircle}>
          {orgCoversAllFees ? (
            <AlertBodyText>
              {'All payouts to your connected debit card will be sent at'}
              <AlertBodyText as="span" $bold>
                {' '}
                no cost.
              </AlertBodyText>
            </AlertBodyText>
          ) : (
            <AlertBodyText>
              {`All payouts to your connected debit card will incur a ${feePercentage} ${baseFee} fee off of the total amount.`}
            </AlertBodyText>
          )}
        </Alert>
      ),
      handleAction() {
        if (!branch_direct_card_account) {
          return showDialog({
            children: (
              <Box style={{ maxWidth: 550 }}>
                <DirectCardPrimerModalContent />
              </Box>
            ),
            size: 'content',
          })
        }
        return updatePaymentProfile.mutateAsync(
          {
            active_type: 'CARD',
            from_type: active_type,
          },
          {
            onSuccess: () =>
              addToast({
                variant: 'success',
                title: 'Your card for payouts has been successfully updated',
              }),
          }
        ) as unknown as Promise<void>
      },
    })
  }

  const updateLinkedCard = () => {
    showAlert({
      title: 'Are you sure?',
      description:
        'Updating your connected debit card will delete your current debit card information from your account.',
      actionDescription: 'Yes, Continue',
      cancelDescription: 'No, cancel',
      handleAction() {
        showDialog({
          children: <DebitCardModalContent type="PAYOUT_CARD" />,
          hideCloseButton: true,
        })
      },
    })
  }

  const disableInstantPay = () => {
    const successTitle = `You've been successfully unenrolled from ${active_type === 'CARD' ? `payouts via ${orgInfo?.app_display_name}` : 'direct payouts.'}`
    const description = `You are about to unenroll from ${active_type === 'CARD' ? `${orgInfo?.app_display_name}` : 'direct'} payouts.`
    showAlert({
      title: 'Are you sure?',
      description,
      actionDescription: 'Yes, Turn Off',
      cancelDescription: 'No, Cancel',
      handleAction() {
        return updatePaymentProfile.mutateAsync(
          {
            active_type: 'NONE',
            from_type: active_type,
          },
          {
            onSuccess: () =>
              addToast({ variant: 'success', title: successTitle }),
          }
        ) as unknown as Promise<void>
      },
      isDestructive: true,
      children: (
        <Alert Icon={InfoCircle}>
          <AlertBodyText>
            {`To continue receiving payments, please set up an alternative payment method with ${orgName}.`}
          </AlertBodyText>
        </Alert>
      ),
    })
  }

  const switchToWallet = () => {
    if (!wallet) {
      return showDialog({
        children: <AppPayoutsModalContent />,
      })
    }
    showAlert({
      title: 'Are you sure?',
      description: `Updating your payout method to the ${orgInfo?.app_display_name} app will send all incoming payouts to your ${accountTypeDisplay} account ending in ${formatAccountNumberLast4(
        wallet?.account_number
      )}.`,
      actionDescription: 'Yes, Continue',
      cancelDescription: 'No, Cancel',
      handleAction() {
        return updatePaymentProfile.mutateAsync(
          {
            active_type: 'WALLET',
            from_type: active_type,
          },
          {
            onSuccess: () =>
              addToast({
                variant: 'success',
                title: `Your payouts have successfully been switched to your ${orgInfo?.app_display_name} account`,
              }),
          }
        ) as unknown as Promise<void>
      },
    })
  }
  return {
    switchToDebitCard,
    disableInstantPay,
    updateLinkedCard,
    switchToWallet,
  }
}
